// Portal copy of the storefront plugin customizer contract.
//
// SOURCE OF TRUTH (keep in sync, separate repo):
//   storefront-plugin/src/theme/tokens.css  → THEME_TOKEN_GROUPS (every --wm-* token + default)
//   storefront-plugin/src/lib/labels.ts     → LABEL_SECTIONS / LABEL_FIELDS (LABEL_DEFS, LABEL_KEYS)
//
// Both live in `brand_plugin_config.theme`: tokens as `--wm-*` keys (CSS custom
// properties) and section text as `wm-label-*` keys (no `--`, so the bundle never
// emits them as CSS). Only non-default values are saved.

/**
 * How the customizer edits a token. None of them ask the brand for CSS:
 *   color   → colour picker
 *   length  → number of pixels
 *   font    → preset list, or a plain font name
 *   shadow  → None / Soft / Medium / Strong
 *   padding → two pixel numbers (height, width)
 *   shape   → Pill / Rounded / Square
 */
export type ThemeTokenType = 'color' | 'length' | 'font' | 'shadow' | 'padding' | 'shape';

export interface ThemeTokenDef {
    key: string;
    label: string;
    type: ThemeTokenType;
    /** tokens.css default. '' = unset (inherits the storefront font). */
    default: string;
    /** When the default is `var(--wm-x)`, the token it follows. */
    follows?: string;
    hint?: string;
}

/** Shadow choices (the CSS is ours, the brand only sees the names). */
export const SHADOW_PRESETS: ReadonlyArray<{ value: string; label: string }> = [
    { value: 'none', label: 'None' },
    { value: '0 2px 6px rgba(0, 0, 0, 0.08)', label: 'Soft' },
    { value: '0 4px 16px rgba(0, 0, 0, 0.12)', label: 'Medium' },
    { value: '0 8px 28px rgba(0, 0, 0, 0.22)', label: 'Strong' },
];

/** Corner choices for the scale pills. */
export const SHAPE_PRESETS: ReadonlyArray<{ value: string; label: string }> = [
    { value: '999px', label: 'Pill' },
    { value: '6px', label: 'Rounded' },
    { value: '0px', label: 'Square' },
];

export interface ThemeTokenGroup {
    id: string;
    title: string;
    tokens: ThemeTokenDef[];
}

// #region THEME_TOKEN_GROUPS
export const THEME_TOKEN_GROUPS: ThemeTokenGroup[] = [
    {
        id: 'colors',
        title: 'Colors',
        tokens: [
            { key: '--wm-color-primary', label: 'Primary', type: 'color', default: '#1a1a1a', hint: 'Selected cards; buttons follow it by default' },
            { key: '--wm-color-text', label: 'Text', type: 'color', default: '#1a1a1a' },
            { key: '--wm-color-muted', label: 'Muted text', type: 'color', default: '#6b6b6b', hint: 'Labels and secondary text' },
            { key: '--wm-color-border', label: 'Borders', type: 'color', default: '#d9d9d9', hint: 'Inputs, cards and table rules' },
            { key: '--wm-color-bg', label: 'Card & input background', type: 'color', default: '#ffffff', hint: 'Cards, inputs, scale options and the preview dialog. The plugin itself has no background — your page shows through.' },
            { key: '--wm-color-accent', label: 'Accent', type: 'color', default: '#8a6d3b', hint: 'Focus rings and "You Save"' },
            { key: '--wm-color-strike', label: 'Strikethrough price', type: 'color', default: '#9a9a9a' },
            { key: '--wm-color-error', label: 'Errors', type: 'color', default: '#b42318' },
        ],
    },
    {
        id: 'typography',
        title: 'Typography',
        tokens: [
            { key: '--wm-font-family', label: 'Font family', type: 'font', default: '', hint: 'Unset = your store font' },
            { key: '--wm-font-size', label: 'Base font size', type: 'length', default: '14px' },
            { key: '--wm-heading-size', label: 'Section heading size', type: 'length', default: '16px' },
        ],
    },
    {
        id: 'shape',
        title: 'Shape & Spacing',
        tokens: [
            { key: '--wm-radius', label: 'Corner radius', type: 'length', default: '6px' },
            { key: '--wm-spacing', label: 'Spacing', type: 'length', default: '12px' },
            { key: '--wm-shadow', label: 'Popover shadow', type: 'shadow', default: '0 4px 16px rgba(0, 0, 0, 0.12)' },
        ],
    },
    {
        id: 'buttons',
        title: 'Buttons',
        tokens: [
            { key: '--wm-button-bg', label: 'Button background', type: 'color', default: 'var(--wm-color-primary)', follows: '--wm-color-primary' },
            { key: '--wm-button-text', label: 'Button text', type: 'color', default: '#ffffff' },
            { key: '--wm-button-height', label: 'Button height', type: 'length', default: '48px' },
            { key: '--wm-button-radius', label: 'Button corner radius', type: 'length', default: 'var(--wm-radius)', follows: '--wm-radius' },
        ],
    },
    {
        id: 'scale',
        title: 'Scale picker',
        tokens: [
            { key: '--wm-pill-radius', label: 'Scale option shape', type: 'shape', default: '999px' },
            { key: '--wm-pill-padding', label: 'Scale option size', type: 'padding', default: '8px 16px', hint: 'Space inside each option' },
            { key: '--wm-pill-min-width', label: 'Scale option minimum width', type: 'length', default: '0px', hint: '0 = fit the text; set a width so every option matches' },
            { key: '--wm-color-recommended', label: 'Recommended scale option', type: 'color', default: 'var(--wm-color-accent)', follows: '--wm-color-accent', hint: 'Always shown on the recommended scale, selected or not' },
            { key: '--wm-color-recommended-text', label: 'Recommended scale text', type: 'color', default: '#ffffff' },
            { key: '--wm-color-silhouette', label: 'Scale preview silhouette', type: 'color', default: 'rgba(0, 0, 0, 0.55)', hint: 'The door / person / sofa drawn on the wall preview' },
        ],
    },
    {
        id: 'badges',
        title: 'Badges & Swatches',
        tokens: [
            { key: '--wm-color-badge', label: '"Recommended" badge', type: 'color', default: 'var(--wm-color-accent)', follows: '--wm-color-accent' },
            { key: '--wm-color-badge-text', label: 'Badge text', type: 'color', default: '#ffffff' },
            { key: '--wm-color-placeholder', label: 'Image placeholder', type: 'color', default: '#efe9df', hint: 'Colorway / product-type circle without an image' },
            { key: '--wm-swatch-size', label: 'Colorway swatch size', type: 'length', default: '40px' },
        ],
    },
];

export const THEME_TOKENS: ThemeTokenDef[] = THEME_TOKEN_GROUPS.flatMap((group: ThemeTokenGroup) => group.tokens);

// #region THEME_PRESETS
/**
 * One-click looks. SOURCE OF TRUTH: storefront-plugin/src/theme/presets.ts —
 * keep identical. Applying a preset replaces every `--wm-*` value in the draft
 * (labels untouched); "Classic" clears them all.
 */
export interface ThemePreset {
    id: string;
    name: string;
    description: string;
    /** Background, primary, accent — shown on the preset card. */
    swatches: [string, string, string];
    theme: Record<string, string>;
}

export const THEME_PRESETS: ReadonlyArray<ThemePreset> = [
    {
        id: 'classic',
        name: 'Classic',
        description: 'The default look: neutral, understated, inherits your store font.',
        swatches: ['#ffffff', '#1a1a1a', '#8a6d3b'],
        theme: {},
    },
    {
        id: 'minimal',
        name: 'Modern Minimal',
        description: 'Sharp corners, hairline borders, black on white, no shadows.',
        swatches: ['#ffffff', '#111111', '#111111'],
        theme: {
            '--wm-color-primary': '#111111',
            '--wm-color-text': '#111111',
            '--wm-color-muted': '#7a7a7a',
            '--wm-color-border': '#e4e4e4',
            '--wm-color-bg': '#ffffff',
            '--wm-color-accent': '#111111',
            '--wm-color-strike': '#b0b0b0',
            '--wm-font-family': 'system-ui, -apple-system, "Segoe UI", sans-serif',
            '--wm-radius': '0px',
            '--wm-spacing': '14px',
            '--wm-shadow': 'none',
            '--wm-button-height': '52px',
            '--wm-button-radius': '0px',
            '--wm-color-badge': '#111111',
            '--wm-color-placeholder': '#f3f3f3',
            '--wm-pill-radius': '0px',
            '--wm-pill-padding': '12px 18px',
            '--wm-pill-min-width': '84px',
            '--wm-color-recommended': '#111111',
            '--wm-color-silhouette': 'rgba(17, 17, 17, 0.5)',
        },
    },
    {
        id: 'warm',
        name: 'Soft & Warm',
        description: 'Cream background, rounded corners, terracotta highlights, serif type.',
        swatches: ['#fbf7f2', '#5e4634', '#c2703d'],
        theme: {
            '--wm-color-primary': '#5e4634',
            '--wm-color-text': '#3f3129',
            '--wm-color-muted': '#8c7b6e',
            '--wm-color-border': '#e8ddd2',
            '--wm-color-bg': '#fbf7f2',
            '--wm-color-accent': '#c2703d',
            '--wm-color-strike': '#b5a799',
            '--wm-font-family': 'Georgia, "Times New Roman", serif',
            '--wm-radius': '14px',
            '--wm-spacing': '14px',
            '--wm-shadow': '0 8px 28px rgba(94, 70, 52, 0.18)',
            '--wm-button-height': '50px',
            '--wm-button-radius': '999px',
            '--wm-color-badge': '#c2703d',
            '--wm-color-placeholder': '#f1e8dd',
            '--wm-swatch-size': '44px',
            '--wm-pill-radius': '999px',
            '--wm-pill-padding': '10px 18px',
            '--wm-color-recommended': '#c2703d',
            '--wm-color-silhouette': 'rgba(94, 70, 52, 0.45)',
        },
    },
    {
        id: 'studio',
        name: 'Bold Studio',
        description: 'High-contrast editorial look on white: black outlines, black buttons, a gold accent.',
        swatches: ['#ffffff', '#0a0a0a', '#d4a017'],
        theme: {
            '--wm-color-primary': '#0a0a0a',
            '--wm-color-text': '#0a0a0a',
            '--wm-color-muted': '#4a4a4a',
            '--wm-color-border': '#0a0a0a',
            '--wm-color-bg': '#ffffff',
            '--wm-color-accent': '#d4a017',
            '--wm-color-strike': '#8a8a8a',
            '--wm-font-family': '"Helvetica Neue", Helvetica, Arial, sans-serif',
            '--wm-font-size': '15px',
            '--wm-heading-size': '18px',
            '--wm-radius': '0px',
            '--wm-spacing': '16px',
            '--wm-shadow': '8px 8px 0 rgba(10, 10, 10, 1)',
            '--wm-button-bg': '#0a0a0a',
            '--wm-button-text': '#ffffff',
            '--wm-button-height': '56px',
            '--wm-button-radius': '0px',
            '--wm-color-badge': '#d4a017',
            '--wm-color-badge-text': '#0a0a0a',
            '--wm-color-placeholder': '#f0f0f0',
            '--wm-swatch-size': '44px',
            '--wm-pill-radius': '0px',
            '--wm-pill-padding': '12px 20px',
            '--wm-pill-min-width': '88px',
            '--wm-color-recommended': '#d4a017',
            '--wm-color-recommended-text': '#0a0a0a',
            '--wm-color-silhouette': 'rgba(10, 10, 10, 0.6)',
        },
    },
    {
        id: 'coastal',
        name: 'Coastal',
        description: 'Navy and teal on white, airy spacing, pill buttons.',
        swatches: ['#ffffff', '#1f3a5f', '#2a9d8f'],
        theme: {
            '--wm-color-primary': '#1f3a5f',
            '--wm-color-text': '#1c2b3a',
            '--wm-color-muted': '#5b6b7c',
            '--wm-color-border': '#d3dce6',
            '--wm-color-bg': '#ffffff',
            '--wm-color-accent': '#2a9d8f',
            '--wm-color-strike': '#9fb0c0',
            '--wm-font-family': 'Helvetica, Arial, sans-serif',
            '--wm-radius': '10px',
            '--wm-spacing': '16px',
            '--wm-shadow': '0 6px 20px rgba(31, 58, 95, 0.14)',
            '--wm-button-height': '50px',
            '--wm-button-radius': '999px',
            '--wm-color-badge': '#2a9d8f',
            '--wm-color-placeholder': '#eef3f8',
            '--wm-pill-radius': '10px',
            '--wm-pill-padding': '10px 16px',
            '--wm-color-recommended': '#2a9d8f',
            '--wm-color-silhouette': 'rgba(31, 58, 95, 0.4)',
        },
    },
    {
        id: 'botanical',
        name: 'Botanical',
        description: 'Deep green and sage on a pale leaf background; calm and natural.',
        swatches: ['#f5f8f2', '#2f4a3a', '#8aa36b'],
        theme: {
            '--wm-color-primary': '#2f4a3a',
            '--wm-color-text': '#223328',
            '--wm-color-muted': '#6b7a6b',
            '--wm-color-border': '#d6dfcf',
            '--wm-color-bg': '#f5f8f2',
            '--wm-color-accent': '#8aa36b',
            '--wm-color-strike': '#a9b5a3',
            '--wm-font-family': 'Georgia, serif',
            '--wm-radius': '12px',
            '--wm-spacing': '14px',
            '--wm-shadow': '0 8px 24px rgba(47, 74, 58, 0.16)',
            '--wm-button-height': '50px',
            '--wm-button-radius': '12px',
            '--wm-color-badge': '#8aa36b',
            '--wm-color-placeholder': '#e9efe3',
            '--wm-swatch-size': '44px',
            '--wm-pill-radius': '12px',
            '--wm-pill-padding': '10px 16px',
            '--wm-color-recommended': '#8aa36b',
            '--wm-color-silhouette': 'rgba(47, 74, 58, 0.45)',
        },
    },
];

// #region applyPreset
/** The draft with every `--wm-*` value replaced by the preset's (section text kept). */
export const applyPreset = (draft: Record<string, string | number>, preset: ThemePreset): Record<string, string | number> => {
    const next: Record<string, string | number> = {};
    Object.entries(draft).forEach(([key, value]) => {
        if (!key.startsWith('--wm-')) next[key] = value;
    });
    return { ...next, ...preset.theme };
};

// #region Images
/**
 * Brand-supplied image URLs, stored in the theme like labels (`wm-image-*`, never
 * CSS). SOURCE OF TRUTH: storefront-plugin/src/lib/images.ts (IMAGE_KEYS).
 */
export const IMAGE_PREFIX = 'wm-image-';
export const IMAGE_MAX_LENGTH = 1000;

export interface ImageFieldDef {
    key: string;
    label: string;
    hint: string;
}

export const IMAGE_FIELDS: ImageFieldDef[] = [
    { key: 'wm-image-product-type-wallpaper', label: 'Wallpaper icon', hint: 'Shown in the Product type circle for wallpaper' },
    { key: 'wm-image-product-type-giftwrap', label: 'Wrapping paper icon', hint: 'Shown in the Product type circle for wrapping paper' },
    { key: 'wm-image-product-type-fabric', label: 'Fabric icon', hint: 'Shown in the Product type circle for fabric' },
];

/** Same rule as the bundle: absolute http(s), no quotes / brackets / spaces, ≤ 1000 chars. */
export const isValidImageUrl = (value: string): boolean => {
    const v = value.trim();
    if (!v || v.length > IMAGE_MAX_LENGTH || /["'()<>\\\s]/.test(v)) return false;
    try {
        const url = new URL(v);
        return url.protocol === 'https:' || url.protocol === 'http:';
    } catch {
        return false;
    }
};

// #region Labels
export const LABEL_PREFIX = 'wm-label-';
export const LABEL_MAX_LENGTH = 200;

export type LabelSection =
    | 'product_types' | 'colorways' | 'scale' | 'dimensions' | 'materials' | 'samples' | 'your_wall' | 'rolls' | 'cart';

/** Section groups in display order (storefront-plugin LABEL_SECTIONS). */
export const LABEL_SECTIONS: ReadonlyArray<{ section: LabelSection; title: string }> = [
    { section: 'product_types', title: 'Product types' },
    { section: 'colorways', title: 'Colorways' },
    { section: 'scale', title: 'Scale' },
    { section: 'dimensions', title: 'Dimensions' },
    { section: 'materials', title: 'Materials' },
    { section: 'samples', title: 'Samples' },
    { section: 'your_wall', title: 'Your Wall' },
    { section: 'rolls', title: 'Roll lengths (wrapping paper)' },
    { section: 'cart', title: 'Cart' },
];

export interface LabelFieldDef {
    /** Theme key, e.g. `wm-label-colorways-title`. */
    key: string;
    label: string;
    default: string;
    section: LabelSection;
}

// [label key (storefront LABEL_DEFS), section, default text, customizer field name]
const LABEL_ROWS: ReadonlyArray<readonly [string, LabelSection, string, string]> = [
    ['product-types-title', 'product_types', 'Product type', 'Title'],
    ['product-type-wallpaper', 'product_types', 'Wallpaper', 'Wallpaper'],
    ['product-type-giftwrap', 'product_types', 'Wrapping Paper', 'Wrapping paper'],
    ['product-type-fabric', 'product_types', 'Fabric', 'Fabric'],

    ['colorways-title', 'colorways', 'Colorways', 'Title'],

    ['scale-title', 'scale', 'Choose Scale', 'Title'],
    ['scale-name', 'scale', 'Scale {scale}', 'Scale name'],
    ['scale-short', 'scale', '{width} {unit}', 'Scale option text'],
    ['scale-recommended', 'scale', 'Recommended', 'Recommended'],
    ['scale-repeat', 'scale', 'Repeat {repeat}', 'Repeat size'],
    ['scale-preview-open', 'scale', 'Preview on your wall', 'Preview link'],
    ['scale-preview-close', 'scale', 'Close', 'Preview close button'],
    ['scale-preview-confirm', 'scale', 'Confirm scale', 'Preview confirm button'],
    ['scale-preview-wall', 'scale', 'On a {width} × {height} {unit} wall', 'Wall preview caption'],
    ['scale-preview-roll', 'scale', 'One {width} {unit} wide panel · top {height} {unit} shown', 'Wrapping paper preview caption'],
    ['scale-ref-door', 'scale', 'Door', 'Reference: door'],
    ['scale-ref-person', 'scale', 'Person', 'Reference: person'],
    ['scale-ref-sofa', 'scale', 'Sofa', 'Reference: sofa'],
    ['scale-ref-phone', 'scale', 'Phone', 'Reference: phone'],
    ['scale-ref-mug', 'scale', 'Mug', 'Reference: mug'],
    ['scale-ref-bottle', 'scale', 'Bottle', 'Reference: bottle'],

    ['dimensions-title', 'dimensions', 'Enter Wallpaper Dimensions', 'Title'],
    ['dimensions-width', 'dimensions', 'Width ({unit})', 'Width field'],
    ['dimensions-height', 'dimensions', 'Height ({unit})', 'Height field'],
    ['dimensions-width-error', 'dimensions', 'Enter a width greater than 0', 'Width error'],
    ['dimensions-height-error', 'dimensions', 'Height must be between {min} and {max} {unit}', 'Height error'],
    ['dimensions-printed', 'dimensions', 'Printed:', 'Printed size prefix'],
    ['dimensions-show-price', 'dimensions', 'Show Price and Material', 'Continue button'],

    ['materials-title', 'materials', 'Choose your material', 'Title'],
    ['materials-empty', 'materials', 'No materials available for this size', 'No materials message'],
    ['materials-unpasted', 'materials', 'Unpasted', 'Unpasted'],
    ['materials-peel-and-stick', 'materials', 'Peel & Stick', 'Peel & stick'],
    ['materials-roll', 'materials', 'Roll', 'Roll'],
    ['materials-sold-out', 'materials', 'Sold out', 'Sold out'],
    ['materials-per-sqft', 'materials', '{price}/sqft', 'Price per sq ft'],

    ['samples-title', 'samples', 'Try Sample', 'Title'],
    ['samples-from', 'samples', 'from {price}', 'Starting price'],
    ['samples-open', 'samples', 'Add +', 'Open button'],
    ['samples-close', 'samples', 'Close', 'Close button'],
    ['samples-scale-title', 'samples', 'Sample scale', 'Scale title'],
    ['samples-add-one', 'samples', 'Add {count} sample to cart – {price}', 'Add button (1 sample)'],
    ['samples-add', 'samples', 'Add {count} samples to cart – {price}', 'Add button (several samples)'],

    ['your-wall-title', 'your_wall', 'Your Wall', 'Title'],
    ['your-wall-print', 'your_wall', 'Print', 'Print row'],
    ['your-wall-dimensions', 'your_wall', 'Dimensions', 'Dimensions row'],
    ['your-wall-material', 'your_wall', 'Material', 'Material row'],
    ['your-wall-scale', 'your_wall', 'Scale', 'Scale row'],
    ['your-wall-panels', 'your_wall', 'Panels', 'Panels row'],
    ['your-wall-area', 'your_wall', 'Area', 'Area row'],
    ['your-wall-rate', 'your_wall', 'Rate', 'Rate row'],
    ['your-wall-save', 'your_wall', 'You Save', 'Savings row'],
    ['your-wall-total', 'your_wall', 'Total', 'Total row'],
    ['your-wall-add', 'your_wall', 'Add to cart', 'Add to cart button'],

    ['rolls-title', 'rolls', 'Choose Roll Length', 'Title'],
    ['rolls-empty', 'rolls', 'No roll lengths available for this design', 'No rolls message'],
    ['rolls-length', 'rolls', '{length} ft roll', 'Roll length'],
    ['rolls-price', 'rolls', '{price} / roll', 'Price per roll'],
    ['rolls-sold-out', 'rolls', 'Sold out', 'Sold out'],
    ['rolls-quantity', 'rolls', 'Rolls', 'Quantity field'],
    ['rolls-add', 'rolls', 'Add to cart – {price}', 'Add to cart button'],
    ['rolls-choose', 'rolls', 'Choose a roll length', 'Choose prompt'],

    ['cart-adding', 'cart', 'Adding…', 'Adding to cart'],
];

export const LABEL_FIELDS: LabelFieldDef[] = LABEL_ROWS.map(([key, section, text, label]) => ({
    key: `${LABEL_PREFIX}${key}`,
    label,
    default: text,
    section,
}));

/** Every customizable key → its default (tokens.css value or label text). */
export const THEME_DEFAULTS: Readonly<Record<string, string>> = Object.freeze(Object.fromEntries([
    ...THEME_TOKENS.map((token: ThemeTokenDef) => [token.key, token.default]),
    ...LABEL_FIELDS.map((field: LabelFieldDef) => [field.key, field.default]),
    ...IMAGE_FIELDS.map((field: ImageFieldDef) => [field.key, '']),
]));

/** `{name}` placeholders in a label default, e.g. ['count', 'price']. */
export const labelPlaceholders = (text: string): string[] =>
    Array.from(text.matchAll(/\{(\w+)\}/g), (match: RegExpMatchArray) => match[1]);
